(function () {
  'use strict';

  function initBestSellers(section) {
    // ── Guard against double-initialisation (theme editor re-runs script) ──
    if (section.dataset.bsInit === 'true') return;
    section.dataset.bsInit = 'true';

    const track = section.querySelector('.bs-track');
    if (!track) return;

    const trackWrapper = section.querySelector('.bs-track-wrapper');
    const origCards = Array.from(track.querySelectorAll('.bs-card'));
    if (origCards.length === 0) return;

    const N = origCards.length;
    const prevBtn = section.querySelector('.bs-arrow--prev');
    const nextBtn = section.querySelector('.bs-arrow--next');

    const isMobile = () => window.innerWidth <= 749;

    const interval = parseInt(section.dataset.autoscrollInterval, 10) || 3000;
    const cardsInFocusDesktop = parseInt(section.dataset.cardsInFocus, 10) || 2;

    // ── Build infinite clone ring ──────────────────────────────────
    origCards.forEach((card) => { track.appendChild(card.cloneNode(true)); });
    for (let i = N - 1; i >= 0; i--) {
      track.insertBefore(origCards[i].cloneNode(true), track.firstChild);
    }
    const allCards = Array.from(track.querySelectorAll('.bs-card'));

    let currentIndex = N;

    // ── Animation state ────────────────────────────────────────────
    let isTransitioning = false;
    let pendingDelta    = null;
    let autoTimer       = null;
    let isPaused        = false;
    let safetyTimer     = null;

    // ── CSS helpers ───────────────────────────────────────────────
    function getBaseCardWidth() {
      const st = getComputedStyle(section);
      if (isMobile()) {
        return parseFloat(st.getPropertyValue('--bs-card-width-mobile')) || 0;
      }
      const base  = parseFloat(st.getPropertyValue('--bs-card-width-desktop')) || 0;
      let   scale = 1;
      if (section.dataset.fluidScaling === 'true') {
        const ref = parseInt(section.dataset.fluidBaseWidth, 10) || 1440;
        if (ref > 0) scale = window.innerWidth / ref;
      }
      return base * scale;
    }

    function getFocusScale() {
      const v = parseFloat(getComputedStyle(section).getPropertyValue('--bs-focus-scale'));
      return (v && v > 0 ? v : 100) / 100;
    }

    function getLayout() {
      const mobile      = isMobile();
      const parentWidth = track.parentElement.offsetWidth;
      const gapPx       = parseFloat(getComputedStyle(track).gap) || 0;
      const focusCount  = Math.max(1, Math.min(mobile ? 1 : cardsInFocusDesktop, N));
      return { gapPx, focusCount, parentWidth };
    }

    // ── Visual helpers ────────────────────────────────────────────
    function updateClasses(idx) {
      const { focusCount } = getLayout();
      allCards.forEach((card, i) => {
        const active = i >= idx && i < idx + focusCount;
        card.classList.toggle('bs-card--active', active);
        card.classList.toggle('bs-card--side',   !active);
      });
    }

    function computeTranslateX(idx) {
      const { gapPx, focusCount, parentWidth } = getLayout();
      const baseW  = getBaseCardWidth();
      if (!baseW) return 0;
      const activeW = baseW * getFocusScale();

      let cursor    = 0;
      let firstLeft = 0;
      let lastRight = 0;
      for (let i = 0; i < allCards.length; i++) {
        const w = (i >= idx && i < idx + focusCount) ? activeW : baseW;
        if (i === idx)                  firstLeft = cursor;
        if (i === idx + focusCount - 1) lastRight = cursor + w;
        cursor += w + gapPx;
      }
      return parentWidth / 2 - (firstLeft + lastRight) / 2;
    }

    // ── Core movement primitives ──────────────────────────────────
    function snapTo(index) {
      currentIndex = index;
      updateClasses(currentIndex);
      isTransitioning = false;
      clearTimeout(safetyTimer);
      track.style.transition = 'none';
      void track.offsetWidth;
      track.style.transform  = `translateX(${computeTranslateX(currentIndex)}px)`;
    }

    function teleport() {
      const offset = ((currentIndex - N) % N + N) % N;
      snapTo(N + offset);
    }

    function animateTo(index) {
      currentIndex = index;
      updateClasses(currentIndex);
      isTransitioning = true;
      track.style.transition = 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
      track.style.transform  = `translateX(${computeTranslateX(currentIndex)}px)`;
      clearTimeout(safetyTimer);
      safetyTimer = setTimeout(onTransitionDone, 700);
    }

    function onTransitionDone() {
      clearTimeout(safetyTimer);
      if (!isTransitioning) return;
      isTransitioning = false;

      if (currentIndex < N || currentIndex >= 2 * N) {
        section.classList.add('bs-instant');
        teleport();
        requestAnimationFrame(() => {
          section.classList.remove('bs-instant');
          if (pendingDelta !== null) {
            const delta = pendingDelta;
            pendingDelta = null;
            animateTo(currentIndex + delta);
          }
        });
        return;
      }

      if (pendingDelta !== null) {
        const delta = pendingDelta;
        pendingDelta = null;
        animateTo(currentIndex + delta);
      }
    }

    // ── Unified navigation entry point ────────────────────────────
    function navigate(delta, isAuto) {
      if (isTransitioning) {
        if (!isAuto) pendingDelta = delta;
        return;
      }
      animateTo(currentIndex + delta);
      if (!isAuto) { stopAuto(); startAuto(); }
    }

    // ── transitionend ─────────────────────────────────────────────
    track.addEventListener('transitionend', (e) => {
      if (e.target !== track || e.propertyName !== 'transform') return;
      onTransitionDone();
    });

    // ── Autoscroll ────────────────────────────────────────────────
    function startAuto() {
      stopAuto();
      autoTimer = setInterval(() => {
        if (!isPaused) navigate(1, true);
      }, interval);
    }

    function stopAuto() {
      clearInterval(autoTimer);
      autoTimer = null;
    }

    // ── Hover pause ───────────────────────────────────────────────
    if (trackWrapper) {
      trackWrapper.addEventListener('mouseenter', () => { isPaused = true;  stopAuto();  });
      trackWrapper.addEventListener('mouseleave', () => { isPaused = false; startAuto(); });
    }

    // ── Arrow buttons ─────────────────────────────────────────────
    prevBtn.addEventListener('click', () => navigate(-1, false));
    nextBtn.addEventListener('click', () => navigate( 1, false));

    // ── Touch / swipe ─────────────────────────────────────────────
    let touchStartX = 0;
    let touchMoved  = false;

    track.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchMoved  = false;
      isPaused = true;
      stopAuto();
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      if (Math.abs(e.touches[0].clientX - touchStartX) > 8) touchMoved = true;
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        navigate(diff > 0 ? 1 : -1, false);
      } else if (!touchMoved) {
        // Treat as tap → navigate to product page
        const card = e.target.closest('.bs-card');
        if (card && card.dataset.url) {
          window.location.href = card.dataset.url;
        }
      }
      isPaused = false;
      startAuto();
    }, { passive: true });

    // ── Card click → product page ─────────────────────────────────
    // The <a class="bs-card__atc"> handles itself natively.
    // This handler catches clicks on any other part of the card.
    track.addEventListener('click', (e) => {
      // Let the native <a> link work without interference
      if (e.target.closest('.bs-card__atc')) return;

      const card = e.target.closest('.bs-card');
      if (!card || !card.dataset.url) return;

      window.location.href = card.dataset.url;
    });

    // ── Wishlist toggle ───────────────────────────────────────────
    track.addEventListener('click', (e) => {
      const btn = e.target.closest('.bs-card__wishlist');
      if (!btn) return;
      btn.classList.toggle('bs-card__wishlist--active');
    });

    // ── Rivyo fallback enrichment ─────────────────────────────────
    // Rivyo exposes no Liquid metafields. For every product whose card
    // rendered WITHOUT a rating, inject a hidden Rivyo badge probe
    // (<div class="wc_product_review_badge">). If Rivyo's app embed is
    // active on the store, its script populates the probe; we then parse
    // the rating + count out of it and inject our own ★ x.x (n) markup
    // into every copy of that product's card. If Rivyo never populates,
    // the probe is removed after 15s and nothing changes.
    function initRivyoFallback() {
      const missing = new Map();
      origCards.forEach((card) => {
        if (!card.querySelector('.bs-card__stars') && card.dataset.productId && card.dataset.handle) {
          missing.set(card.dataset.productId, card.dataset.handle);
        }
      });
      if (missing.size === 0) return;

      const STAR_SVG = '<svg class="bs-star bs-star--filled" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>';

      const probeWrap = document.createElement('div');
      probeWrap.className = 'bs-rivyo-probe';
      probeWrap.setAttribute('aria-hidden', 'true');
      missing.forEach((handle, pid) => {
        const badge = document.createElement('div');
        badge.className = 'wc_product_review_badge';
        badge.setAttribute('data-handle', handle);
        badge.setAttribute('data-product_id', pid);
        badge.setAttribute('data-bs-pid', pid);
        probeWrap.appendChild(badge);
      });
      section.appendChild(probeWrap);

      function parseBadge(badge) {
        const text = (badge.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text && !badge.firstElementChild) return null;

        let rating = null;
        let count  = null;

        // Rating as an explicit decimal, e.g. "4.5"
        const dec = text.match(/([0-5]\.\d{1,2})/);
        if (dec) rating = parseFloat(dec[1]);

        // Count in parentheses, e.g. "(12)", else "12 reviews"
        const paren = text.match(/\((\d{1,6})\)/);
        if (paren) count = parseInt(paren[1], 10);
        if (count === null) {
          const rev = text.match(/(\d{1,6})\s*(?:reviews?|ratings?)/i);
          if (rev) count = parseInt(rev[1], 10);
        }

        // Rating via star-fill width technique, e.g. style="width: 90%"
        if (rating === null) {
          const widthEl = badge.querySelector('[style*="width"]');
          if (widthEl) {
            const wm = (widthEl.getAttribute('style') || '').match(/width\s*:\s*([\d.]+)%/);
            if (wm) rating = Math.round((parseFloat(wm[1]) / 100) * 5 * 10) / 10;
          }
        }

        // Rating via counting filled star icons
        if (rating === null) {
          const stars  = badge.querySelectorAll('i, svg, span[class*="star"]');
          const filled = badge.querySelectorAll('[class*="fill"], [class*="on"], [class*="active"], [class*="checked"]');
          if (stars.length >= 5 && filled.length > 0 && filled.length <= 5) rating = filled.length;
        }

        if (rating === null && count === null) return null;
        return { rating: rating, count: count };
      }

      function applyRating(pid, data) {
        if (!data || !data.rating || data.rating <= 0) return;
        const value = Math.round(data.rating * 10) / 10;
        const hasCount  = typeof data.count === 'number' && data.count > 0;
        const countHTML = hasCount ? '<span class="bs-card__rating-count">(' + data.count + ')</span>' : '';
        const aria = 'Rated ' + value + ' out of 5 stars' + (hasCount ? ' from ' + data.count + ' reviews' : '');
        track.querySelectorAll('.bs-card[data-product-id="' + pid + '"]').forEach((card) => {
          const top = card.querySelector('.bs-card__overlay-top');
          if (!top || top.querySelector('.bs-card__stars')) return;
          const div = document.createElement('div');
          div.className = 'bs-card__stars';
          div.setAttribute('aria-label', aria);
          div.innerHTML = STAR_SVG + '<span class="bs-card__rating-value">' + value + '</span>' + countHTML;
          top.appendChild(div);
        });
      }

      const done = new Set();
      let sweepTimer = null;
      let killTimer  = null;
      let mo         = null;

      function cleanup() {
        if (mo) mo.disconnect();
        clearInterval(sweepTimer);
        clearTimeout(killTimer);
        if (probeWrap.parentNode) probeWrap.parentNode.removeChild(probeWrap);
      }

      function sweep() {
        probeWrap.querySelectorAll('.wc_product_review_badge').forEach((badge) => {
          const pid = badge.getAttribute('data-bs-pid');
          if (done.has(pid)) return;
          const data = parseBadge(badge);
          if (data) {
            done.add(pid);
            applyRating(pid, data);
          }
        });
        if (done.size === missing.size) cleanup();
      }

      mo = new MutationObserver(sweep);
      mo.observe(probeWrap, { childList: true, subtree: true, characterData: true });
      sweepTimer = setInterval(sweep, 1000);
      killTimer  = setTimeout(cleanup, 15000);
    }

    initRivyoFallback();

    // ── Resize ────────────────────────────────────────────────────
    let resizeTimer;
    window.addEventListener('resize', () => {
      section.classList.add('bs-instant');
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const offset = ((currentIndex - N) % N + N) % N;
        isTransitioning = false;
        pendingDelta    = null;
        clearTimeout(safetyTimer);
        snapTo(N + offset);
        requestAnimationFrame(() => { section.classList.remove('bs-instant'); });
      }, 120);
    });

    // ── First paint ───────────────────────────────────────────────
    section.classList.add('bs-instant');
    snapTo(N);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        section.classList.remove('bs-instant');
        section.classList.add('bs-ready');
        startAuto();
      });
    });
  }

  // ── Boot ──────────────────────────────────────────────────────
  document.querySelectorAll('.bs-wrapper').forEach((section) => {
    initBestSellers(section);
  });

})();